import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { currentDateFnsLocale, formatNumber, formatPercent } from '@/i18n/locale';
import { bucketKey, bucketStarts, type Grain } from '@/lib/analyticsPeriod';

/**
 * The call trend of the selected period.
 *
 * Until 2026-10-07 this card carried its OWN period selector (7, 14, 30 or 90
 * days counted from now), applied on top of the screen's period. The two
 * contradicted each other: "6 derniers mois" on the screen still drew 7 days,
 * and "Aujourd'hui" drew 7 days of which 6 were necessarily empty. The chart
 * now follows the screen's period, and picks its own grain so the bars stay
 * readable: hours for a single day, days up to a month, weeks beyond.
 *
 * Two series, not three: calls are the context (grey), reached contacts are
 * what matters (accent). Meetings stay in the tooltip and the subtitle.
 */

interface InteractiveChartsProps {
    /** The server's buckets (POST /api/analytics/dialer/performance, series). */
    series: Array<{ key: string; total: number; connected: number; meetings: number }>;
    grain: Grain;
    /** 'curve' draws lines; anything else draws bars. */
    viewMode?: string;
    periodStart?: Date | null;
    periodEnd?: Date | null;
    firstCallAt?: string | null;
    lastCallAt?: string | null;
}

interface Row {
    key: string;
    tick: string;
    heading: string;
    calls: number;
    connected: number;
    notConnected: number;
    meetings: number;
}

const ACCENT = 'var(--vf-accent)';
const CONTEXT = 'var(--vf-text-4)';
const GRID = 'var(--vf-border)';
const TICK = { fill: 'var(--vf-text-3)', fontSize: 11 };

function ChartTooltip({ active, payload, t }: any) {
    if (!active || !payload || payload.length === 0) return null;
    const row: Row = payload[0].payload;
    const rate = row.calls > 0 ? (row.connected / row.calls) * 100 : 0;
    return (
        <div className="rounded-md border border-border bg-popover text-popover-foreground shadow-md px-3 py-2 text-xs space-y-1">
            <p className="font-medium">{row.heading}</p>
            <p className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: CONTEXT }} />
                {t('pages:analytics.interactiveCharts.tooltipCalls', { count: row.calls, value: formatNumber(row.calls) })}
            </p>
            <p className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: ACCENT }} />
                {t('pages:analytics.interactiveCharts.tooltipReached', { value: formatNumber(row.connected), rate: formatPercent(rate) })}
            </p>
            {row.meetings > 0 && (
                <p className="pl-4" style={{ color: 'var(--vf-text-3)' }}>
                    {t('pages:analytics.interactiveCharts.tooltipMeetings', { value: formatNumber(row.meetings) })}
                </p>
            )}
        </div>
    );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
            {label}
        </span>
    );
}

export default function InteractiveCharts({ series, grain, viewMode, periodStart = null, periodEnd = null, firstCallAt = null, lastCallAt = null }: InteractiveChartsProps) {
    const { t } = useTranslation(['pages', 'dialer']);
    const locale = currentDateFnsLocale();

    // Since W4 the buckets are counted by the server over the whole period;
    // here they are only laid out, the empty ones included.
    const { rows, totals } = useMemo(() => {
        const byKey = new Map(series.map((s) => [s.key, s]));
        const first = firstCallAt ? new Date(firstCallAt) : null;
        const last = lastCallAt ? new Date(lastCallAt) : null;
        const starts = bucketStarts({ start: periodStart, end: periodEnd }, grain, first, last);
        const out: Row[] = starts.map((s) => {
            const key = bucketKey(s, grain);
            const b = byKey.get(key);
            const tick = grain === 'hour' ? t('dialer:analytics.hourLabel', { h: s.getHours() }) : format(s, 'd MMM', { locale });
            const heading = grain === 'hour'
                ? t('dialer:analytics.hourLabel', { h: s.getHours() })
                : grain === 'day'
                    ? format(s, 'EEEE d MMMM', { locale })
                    : t('pages:analytics.interactiveCharts.weekOf', { date: format(s, 'd MMMM', { locale }) });
            const calls = b?.total ?? 0;
            const connected = b?.connected ?? 0;
            return { key, tick, heading, calls, connected, notConnected: calls - connected, meetings: b?.meetings ?? 0 };
        });
        const tot = series.reduce((acc, s) => ({ calls: acc.calls + s.total, connected: acc.connected + s.connected, meetings: acc.meetings + s.meetings }), { calls: 0, connected: 0, meetings: 0 });
        return { rows: out, totals: tot };
    }, [series, grain, periodStart, periodEnd, firstCallAt, lastCallAt, locale, t]);

    const rate = totals.calls > 0 ? (totals.connected / totals.calls) * 100 : 0;
    const best = rows.reduce<Row | null>((acc, r) => (r.connected > (acc?.connected ?? 0) ? r : acc), null);
    const bestKey = grain === 'hour' ? 'bestHour' : grain === 'day' ? 'bestDay' : 'bestWeek';
    const curve = viewMode === 'curve';

    const axisProps = {
        xAxis: {
            dataKey: 'tick',
            tick: TICK,
            axisLine: false,
            tickLine: false,
            interval: 'preserveStartEnd' as const,
            minTickGap: 18,
        },
        yAxis: {
            tick: TICK,
            axisLine: false,
            tickLine: false,
            allowDecimals: false,
            width: 36,
            tickCount: 4,
            tickFormatter: (v: number) => formatNumber(v),
        },
    };

    return (
        <div className="bg-card rounded-xl border border-border p-5 sm:p-6 min-w-0 overflow-hidden" data-testid="analytics-trend-chart">
            <div className="flex items-start justify-between gap-3 mb-4 min-w-0 flex-wrap">
                <div className="min-w-0">
                    <h3 className="text-base font-semibold text-foreground">
                        {totals.calls === 0
                            ? t('pages:analytics.interactiveCharts.emptyTitle')
                            : t('pages:analytics.interactiveCharts.finding', {
                                connected: formatNumber(totals.connected),
                                calls: formatNumber(totals.calls),
                                rate: formatPercent(rate),
                            })}
                    </h3>
                    <p className="text-xs mt-1" style={{ color: 'var(--vf-text-3)' }}>
                        {t('pages:analytics.interactiveCharts.title')}
                        {best && best.connected > 0 && (
                            <> {' · '}{t(`pages:analytics.interactiveCharts.${bestKey}`, { label: best.heading, value: formatNumber(best.connected) })}</>
                        )}
                        {totals.meetings > 0 && (
                            <> {' · '}{t('pages:analytics.interactiveCharts.meetingsTotal', { value: formatNumber(totals.meetings) })}</>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-4 text-xs shrink-0" style={{ color: 'var(--vf-text-2)' }}>
                    <LegendSwatch color={ACCENT} label={t('pages:analytics.interactiveCharts.seriesReached')} />
                    <LegendSwatch color={CONTEXT} label={curve ? t('pages:analytics.interactiveCharts.seriesCalls') : t('pages:analytics.interactiveCharts.seriesNotReached')} />
                </div>
            </div>

            {totals.calls === 0 ? (
                <p className="text-sm text-center py-16" style={{ color: 'var(--vf-text-3)' }}>
                    {t('dialer:analytics.noCallsPeriod')}
                </p>
            ) : (
                <div className="w-full min-w-0">
                    <ResponsiveContainer width="100%" height={280}>
                        {curve ? (
                            <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                                <CartesianGrid stroke={GRID} vertical={false} />
                                <XAxis {...axisProps.xAxis} />
                                <YAxis {...axisProps.yAxis} />
                                <Tooltip content={<ChartTooltip t={t} />} cursor={{ stroke: GRID }} />
                                <Line type="monotone" dataKey="calls" stroke={CONTEXT} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                                <Line type="monotone" dataKey="connected" stroke={ACCENT} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                            </LineChart>
                        ) : (
                            <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="20%">
                                <CartesianGrid stroke={GRID} vertical={false} />
                                <XAxis {...axisProps.xAxis} />
                                <YAxis {...axisProps.yAxis} />
                                <Tooltip content={<ChartTooltip t={t} />} cursor={{ fill: 'var(--vf-surface-2)' }} />
                                <Bar dataKey="connected" stackId="calls" fill={ACCENT} maxBarSize={36} isAnimationActive={false} />
                                <Bar dataKey="notConnected" stackId="calls" fill={CONTEXT} fillOpacity={0.45} radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
